/**
 * Phase 185.e — openSignedDocInNewTab
 *
 * Popup-blocker-proof variant of `openHtmlInNewTab`. Solves the long-standing
 * `Popup blocked. Allow popups…` toast that hits aggressive browsers (Brave,
 * mobile Safari, Chrome with strict popup settings, ad-blocker extensions).
 *
 * Strategy:
 *   1. Pre-mint a 5-minute `document_view`-scoped JWT via
 *      POST /api/documents/sign-view-token (the only async step that needs
 *      auth — uses the user's session Bearer header).
 *   2. Build the self-authenticating URL:
 *        ${REACT_APP_BACKEND_URL}/api/{docPath}?token={viewToken}
 *   3. Trigger native anchor navigation in a fresh tab. Because the URL is
 *      real and the click happens inside the user gesture, popup blockers
 *      have NOTHING to flag.
 *
 * Resilience:
 *   • If token minting fails (network down, session expired), fall back to
 *     the legacy `openHtmlInNewTab` flow so the user STILL gets the
 *     document — just via the auth'd fetch+inject path.
 *   • Also pre-opens a placeholder tab SYNCHRONOUSLY before minting, so
 *     even when minting takes >100ms, the gesture context is preserved.
 *
 * Usage:
 *   onClick={() => openSignedDocInNewTab(`/invoice/${id}/pdf`, {
 *     label: 'Invoice',
 *   })}
 *
 * @param {string} docPath
 *   API path AFTER /api (leading slash required). Example: `/fees/abc/view`.
 * @param {object} [opts]
 * @param {string} [opts.label] Friendly label for placeholder + toasts.
 * @param {Function} [opts.fallbackFetcher]
 *   Optional async function returning HTML or `{data: html}`. Used when
 *   minting fails. Defaults to a `GET ${docPath}` axios call so the legacy
 *   fetch+inject path still wins.
 */
import { toast } from 'sonner';
import api from '@/lib/api';
import { openHtmlInNewTab } from '@/lib/openHtmlInNewTab';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

function _openPlaceholderTab(label) {
  let win = null;
  try {
    // Phase 264 — DO NOT pass `noopener,noreferrer` here. When `noopener` is
    // set, browsers intentionally return `null` from `window.open()` and the
    // pre-opened tab is unrecoverable — it sits stuck on about:blank forever
    // because we can't navigate it. We strip `window.opener` AFTER the
    // navigation (below) to preserve the security intent without losing the
    // window handle. See MDN: window.open#noopener.
    win = window.open('about:blank', '_blank');
    if (win && win.document) {
      win.document.write(
        `<!doctype html><html><head><title>Loading ${label}…</title>` +
        `<meta charset="utf-8" /></head>` +
        `<body style="font-family:-apple-system,Segoe UI,sans-serif;padding:48px;` +
        `color:#475569;background:#f8fafc">` +
        `<h2 style="color:#0f172a;margin:0 0 8px 0">Preparing your ${(label || 'document').toLowerCase()}…</h2>` +
        `<p style="margin:0">Signing a secure short-lived link, this tab will refresh in a moment.</p>` +
        `</body></html>`,
      );
    }
  } catch (_e) {
    win = null;
  }
  return win;
}

export async function openSignedDocInNewTab(docPath, opts = {}) {
  const label = opts.label || 'Document';
  if (!docPath || typeof docPath !== 'string' || !docPath.startsWith('/')) {
    toast.error(`Cannot open ${label.toLowerCase()} — invalid document path`);
    return { ok: false, reason: 'invalid_path' };
  }
  if (!BACKEND_URL) {
    // Misconfigured env — fall back to the legacy fetcher path.
    return openHtmlInNewTab(
      opts.fallbackFetcher
        || (() => api.get(docPath, { responseType: 'text' })),
      { label, errorMessage: opts.errorMessage },
    );
  }

  // 1) Synchronously claim a tab to preserve the user-gesture context.
  const win = _openPlaceholderTab(label);

  // 2) Mint the short-lived view token.
  let mintRes;
  try {
    mintRes = await api.post('/documents/sign-view-token');
  } catch (e) {
    // Tab was opened but mint failed — close the placeholder tab and fall
    // back to the legacy fetch+inject path so the user still gets the doc.
    if (win && !win.closed) {
      try { win.close(); } catch (_e) { /* noop */ }
    }
    return openHtmlInNewTab(
      opts.fallbackFetcher
        || (() => api.get(docPath, { responseType: 'text' })),
      { label, errorMessage: opts.errorMessage },
    );
  }

  const viewToken = mintRes?.data?.view_token;
  if (!viewToken) {
    if (win && !win.closed) {
      try { win.close(); } catch (_e) { /* noop */ }
    }
    toast.error(`Could not sign ${label.toLowerCase()} link`);
    return { ok: false, reason: 'mint_empty' };
  }

  // 3) Build the self-authenticating URL.
  const sep = docPath.includes('?') ? '&' : '?';
  const url = `${BACKEND_URL}/api${docPath}${sep}token=${encodeURIComponent(viewToken)}`;

  // 4a) Preferred path — navigate the pre-opened tab. No popup blocker can
  //     fire here because the tab was opened during the original gesture.
  if (win && !win.closed) {
    try {
      win.location.href = url;
      // Phase 264 — Strip opener AFTER navigation to retain the security
      // intent of `noopener` without losing the window handle pre-navigation.
      try { win.opener = null; } catch (_e) { /* noop */ }
      return { ok: true, mode: 'signed_tab' };
    } catch (_e) {
      // Cross-origin or sandboxed — fall through to anchor click.
    }
  }

  // 4b) Fallback — synthesize a native anchor click. Real URL + user-gesture
  //     timeout typically still passes the popup blocker, AND the URL bar
  //     will show the real backend host (not about:blank).
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    return { ok: true, mode: 'anchor_click' };
  } catch (_e) {
    toast.error(`Popup blocked`, {
      description: `Allow popups for this site to open ${label.toLowerCase()}s in a new tab.`,
    });
    return { ok: false, reason: 'popup_blocked' };
  }
}

export default openSignedDocInNewTab;
